import { Model, TinyMCE, Toast, Widget, updateWidgetContent } from 'jamespot-front-business';
import jApi, { WidgetArticleTextContent, WidgetArticleTextType, WidgetDisplayMode } from 'jamespot-user-api';
import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';
import styled, { useTheme } from 'styled-components';

import { commonComponentsRegistry } from '../../../registry/CommonRegistry';
import { getTinyMCEMentionsQueries } from '../../../utils/tinymce';
import { useMarkdownModal } from '../../../hooks/useMarkdownModal';
import { MarkdownModal } from '../../tinymce/extension/MarkdownModal';
import { useAppDispatch, useAppSelector } from '../../../redux/store';

const WidgetArticleText = commonComponentsRegistry.getLazyComponent('WidgetArticleText');

const CSSTextWrapper = styled.div`
    width: 100%;
    cursor: text;

    p {
        word-wrap: break-word;
    }

    a {
        word-wrap: break-word;
    }
`;

export const JRCWidgetArticleTextWrapper = ({
    widget,
    mode,
}: {
    widget: WidgetArticleTextType;
    inplace: boolean;
    mode?: WidgetDisplayMode;
}) => {
    const { uniqid } = widget;
    const theme = useTheme();
    const { text } = widget.content;
    const dispatch = useAppDispatch();
    const isActive = useAppSelector((state) => Widget.selectors.isActive(state, uniqid), shallowEqual);
    const isBusy = useAppSelector((state) => Widget.selectors.selectWidgetState(state, uniqid)?.busy);
    const token = useAppSelector(Widget.selectors.selectToken);
    const tinyMCECommonOptions = useAppSelector(TinyMCE.selectors.selectTinyMCECommonOptions);
    const models = useAppSelector(Model.selectors.selectAll);

    const intl = useIntl();

    const {
        open,
        onInsert,
        close: closeMarkdownModal,
        ext: extMarkdown,
        isActive: isMarkdownActive,
    } = useMarkdownModal();

    const onError = useCallback(
        (message?: string) => {
            dispatch(Toast.actions.error({ label: message ?? intl.formatMessage({ id: 'GLOBAL_Error' }) }));
        },
        [dispatch, intl],
    );

    useEffect(() => {
        dispatch(
            Widget.slice.actions.setWidgetState({
                uniqid,
                empty: !text || text.length === 0,
            }),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text]);

    const tinyMentionsQueries = useMemo(() => {
        return getTinyMCEMentionsQueries(models, theme, onError);
    }, [models, onError, theme]);

    // small hack to hide and redraw tinymce to avoid initialization issue
    if (isBusy) {
        return <></>;
    }
    return (
        <CSSTextWrapper>
            <WidgetArticleText
                autoFocus={isActive}
                text={text ?? ''}
                token={mode === 'edit' && token !== undefined ? token : ''}
                tinyMCECommonOptions={
                    mode === 'edit' && tinyMCECommonOptions
                        ? {
                              commonOptions: tinyMCECommonOptions,
                              mentionsQueries: tinyMentionsQueries,
                          }
                        : undefined
                }
                mode={mode}
                onChange={(text) => {
                    dispatch(updateWidgetContent<WidgetArticleTextContent>(uniqid, { text }));
                }}
                additionalExtensions={[extMarkdown]}
                handlers={{ file: jApi.file }}
            />
            {isMarkdownActive && <MarkdownModal onInsert={onInsert} open={open} close={closeMarkdownModal} />}
        </CSSTextWrapper>
    );
};
